import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import { StarIcon } from './StarIcon.js';
import { ChipIcon } from './ChipIcon.js';
import { useTopup } from '../lib/useTopup.js';
import { haptic } from '../lib/telegram.js';
import { api } from '../lib/api.js';
import { useToast } from '../stores/toast.js';

interface TopupSheetProps {
  open: boolean;
  onClose: () => void;
}

type Tab = 'stars' | 'crypto' | 'ton';

const STAR_PRESETS: readonly number[] = [50, 100, 500, 1000];
const MAX_STARS = 50_000;

// chipsPer is shown only as a UX hint — the server is the source of truth at credit time.
const CRYPTO_ASSETS = [
  { code: 'USDT', label: 'USDT', presets: [1, 5, 10, 25], chipsPer: 85 },
  { code: 'TON',  label: 'TON',  presets: [0.5, 1, 5, 10], chipsPer: 450 },
  { code: 'BTC',  label: 'BTC',  presets: [0.0001, 0.0005, 0.001, 0.005], chipsPer: 8_500_000 },
  { code: 'ETH',  label: 'ETH',  presets: [0.005, 0.01, 0.05, 0.1], chipsPer: 280_000 },
] as const;

type CryptoCode = (typeof CRYPTO_ASSETS)[number]['code'];

function formatAsset(amount: number): string {
  if (amount >= 1) return amount.toString();
  return amount.toFixed(amount < 0.01 ? 4 : 3);
}

function formatChips(n: number): string {
  return Math.floor(n).toLocaleString('ru-RU');
}

export function TopupSheet({ open, onClose }: TopupSheetProps) {
  const [tab, setTab] = useState<Tab>('stars');
  const { inFlight: starsInFlight, topup: topupStars } = useTopup();
  const [starsCustom, setStarsCustom] = useState<string>('');

  const [cryptoAsset, setCryptoAsset] = useState<CryptoCode>('USDT');
  const [cryptoCustom, setCryptoCustom] = useState<string>('');
  const [cryptoBusy, setCryptoBusy] = useState(false);
  const pushToast = useToast((s) => s.push);

  // TON tab — generate a deposit intent (address + comment) and show it as instructions.
  const [tonAmount, setTonAmount] = useState<string>('1');
  const [tonBusy, setTonBusy] = useState(false);
  const [tonIntent, setTonIntent] = useState<{ address: string; comment: string; expectedStars: number } | null>(null);

  const tonAmountValue = (() => {
    const n = Number.parseFloat(tonAmount);
    return Number.isFinite(n) && n > 0 && n <= 10_000 ? n : null;
  })();

  const generateTonIntent = async (): Promise<void> => {
    if (tonAmountValue === null) return;
    haptic('medium');
    setTonBusy(true);
    try {
      const intent = await api.tonDepositIntent(tonAmountValue);
      setTonIntent(intent);
    } catch (err) {
      pushToast(err instanceof Error ? err.message : 'TON-канал недоступен', 'error');
    } finally {
      setTonBusy(false);
    }
  };

  const copyToClipboard = async (text: string): Promise<void> => {
    try {
      await navigator.clipboard.writeText(text);
      haptic('light');
      pushToast('Скопировано', 'info');
    } catch {
      pushToast('Не удалось скопировать', 'error');
    }
  };

  const starsCustomAmount = (() => {
    const n = Number.parseInt(starsCustom, 10);
    return Number.isFinite(n) && n > 0 && n <= MAX_STARS ? n : null;
  })();

  const cryptoCustomAmount = (() => {
    const n = Number.parseFloat(cryptoCustom);
    return Number.isFinite(n) && n > 0 && n <= 100_000 ? n : null;
  })();

  const launchStars = async (amount: number): Promise<void> => {
    haptic('medium');
    const outcome = await topupStars(amount);
    if (outcome === 'paid') {
      setStarsCustom('');
      onClose();
    }
  };

  const launchCrypto = async (amount: number): Promise<void> => {
    haptic('medium');
    setCryptoBusy(true);
    try {
      const { invoiceUrl, starsExpected } = await api.cryptoBotTopup(cryptoAsset, amount);
      pushToast(`Откроется CryptoBot — после оплаты зачислим ${starsExpected} фишек`, 'info');
      // CryptoBot pay-url opens via Telegram openTelegramLink or as a regular external link.
      window.open(invoiceUrl, '_blank');
      onClose();
    } catch (err) {
      pushToast(err instanceof Error ? err.message : 'Не удалось создать счёт', 'error');
    } finally {
      setCryptoBusy(false);
    }
  };

  const activeAsset = CRYPTO_ASSETS.find((a) => a.code === cryptoAsset);

  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            onClick={onClose}
            className="fixed inset-0 z-40"
            style={{ background: 'rgba(0, 0, 0, 0.55)' }}
          />
          <motion.div
            initial={{ y: '100%' }}
            animate={{ y: 0 }}
            exit={{ y: '100%' }}
            transition={{ type: 'spring', stiffness: 320, damping: 32 }}
            className="fixed inset-x-0 bottom-0 z-50 mx-auto w-full max-w-[480px] flex flex-col gap-4 px-5 pb-6 pt-3"
            style={{
              background:
                'linear-gradient(180deg, color-mix(in oklab, var(--color-felt-deep) 95%, white 4%) 0%, var(--color-felt-deep) 100%)',
              borderTopLeftRadius: 24,
              borderTopRightRadius: 24,
              borderTop: '1px solid color-mix(in oklab, var(--color-gold) 35%, transparent)',
              boxShadow: '0 -20px 40px -20px rgba(0,0,0,0.7)',
              paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 16px)',
            }}
          >
            <div className="mx-auto h-1 w-10 rounded-full bg-ivory/25" />

            <div className="flex flex-col gap-1">
              <h2 className="heading-display text-2xl">Купить фишки</h2>
              <p className="text-xs text-ivory/55">
                Внутренняя валюта игры — фишки. Купить за Telegram Stars, USDT, TON или другую крипту.
                Это <strong>не</strong> Telegram Stars: фишки нельзя перевести вне приложения.{' '}
                <Link to="/balance" onClick={onClose} className="text-gold underline">
                  Подробнее
                </Link>
              </p>
            </div>

            <div
              className="flex rounded-full p-1"
              style={{
                background: 'var(--color-glass)',
                border: '1px solid color-mix(in oklab, var(--color-gold) 25%, transparent)',
              }}
            >
              {(['stars', 'crypto', 'ton'] as const).map((id) => (
                <button
                  key={id}
                  onClick={() => {
                    haptic('light');
                    setTab(id);
                  }}
                  className="flex-1 rounded-full py-2 text-xs transition-colors"
                  style={{
                    background: tab === id ? 'var(--color-gold)' : 'transparent',
                    color: tab === id ? 'var(--color-felt-deep)' : 'var(--color-ivory)',
                    fontWeight: tab === id ? 700 : 500,
                  }}
                >
                  {id === 'stars' ? 'Stars' : id === 'crypto' ? 'CryptoBot' : 'TON'}
                </button>
              ))}
            </div>

            {tab === 'stars' && (
              <>
                <div className="grid grid-cols-2 gap-2">
                  {STAR_PRESETS.map((amt) => (
                    <button
                      key={amt}
                      disabled={starsInFlight}
                      onClick={() => void launchStars(amt)}
                      className="flex items-center justify-center gap-2 rounded-2xl py-3 disabled:opacity-50"
                      style={{
                        background: 'var(--color-glass)',
                        border: '1px solid color-mix(in oklab, var(--color-gold) 40%, transparent)',
                        fontFamily: 'var(--font-display)',
                        fontSize: 18,
                        color: 'var(--color-ivory)',
                      }}
                    >
                      {amt}
                      <StarIcon size={16} />
                    </button>
                  ))}
                </div>
                <div className="flex flex-col gap-2">
                  <label className="text-xs uppercase tracking-wider text-ivory/55">Своя сумма</label>
                  <div className="flex items-center gap-2">
                    <input
                      type="number"
                      inputMode="numeric"
                      min={1}
                      max={MAX_STARS}
                      value={starsCustom}
                      onChange={(e) => setStarsCustom(e.target.value.replace(/[^0-9]/g, ''))}
                      placeholder="например, 250"
                      className="flex-1 rounded-2xl px-4 py-3 text-base outline-none"
                      style={{
                        background: 'var(--color-glass)',
                        border: '1px solid color-mix(in oklab, var(--color-gold) 25%, transparent)',
                        color: 'var(--color-ivory)',
                        fontFamily: 'var(--font-display)',
                      }}
                    />
                    <button
                      disabled={starsInFlight || starsCustomAmount === null}
                      onClick={() => starsCustomAmount !== null && void launchStars(starsCustomAmount)}
                      className="btn-primary"
                      style={{ minWidth: 90 }}
                    >
                      Купить
                      <StarIcon size={14} />
                    </button>
                  </div>
                  {starsCustomAmount === null && starsCustom.length > 0 && (
                    <p className="text-[11px] text-coral/85">
                      Сумма должна быть от 1 до {MAX_STARS.toLocaleString('ru-RU')} ⭐
                    </p>
                  )}
                  <p className="text-[11px] text-ivory/45 flex items-center gap-1 flex-wrap">
                    1 ⭐ → 1 фишка <ChipIcon size={10} />. Удобно — оплата в один тап.
                    Через CryptoBot/TON цена фишки ≈ 1.8× меньше.{' '}
                    <Link to="/balance" onClick={onClose} className="text-gold underline">
                      Сравнить
                    </Link>
                  </p>
                </div>
              </>
            )}

            {tab === 'crypto' && (
              <>
                <div className="flex flex-wrap gap-1.5">
                  {CRYPTO_ASSETS.map((a) => (
                    <button
                      key={a.code}
                      onClick={() => {
                        haptic('light');
                        setCryptoAsset(a.code);
                        setCryptoCustom('');
                      }}
                      className="rounded-full px-3 py-1.5 text-xs transition-colors"
                      style={{
                        background: cryptoAsset === a.code ? 'var(--color-gold)' : 'var(--color-glass)',
                        color: cryptoAsset === a.code ? 'var(--color-felt-deep)' : 'var(--color-ivory)',
                        border: '1px solid color-mix(in oklab, var(--color-gold) 40%, transparent)',
                        fontFamily: 'var(--font-display)',
                      }}
                    >
                      {a.label}
                    </button>
                  ))}
                </div>
                <div className="grid grid-cols-2 gap-2">
                  {(activeAsset?.presets ?? []).map((amt) => {
                    const chips = activeAsset ? amt * activeAsset.chipsPer : 0;
                    return (
                      <button
                        key={amt}
                        disabled={cryptoBusy}
                        onClick={() => void launchCrypto(amt)}
                        className="flex flex-col items-center justify-center gap-0.5 rounded-2xl py-2.5 disabled:opacity-50"
                        style={{
                          background: 'var(--color-glass)',
                          border: '1px solid color-mix(in oklab, var(--color-gold) 40%, transparent)',
                          fontFamily: 'var(--font-display)',
                          color: 'var(--color-ivory)',
                        }}
                      >
                        <span className="flex items-center gap-1.5 text-base">
                          {formatAsset(amt)} <span className="text-ivory/55 text-xs">{cryptoAsset}</span>
                        </span>
                        <span className="text-[10px] text-gold/85">≈ {formatChips(chips)} фишек</span>
                      </button>
                    );
                  })}
                </div>
                <div className="flex flex-col gap-2">
                  <label className="text-xs uppercase tracking-wider text-ivory/55">
                    Своя сумма ({cryptoAsset})
                  </label>
                  <div className="flex items-center gap-2">
                    <input
                      type="number"
                      inputMode="decimal"
                      step="any"
                      min={0}
                      value={cryptoCustom}
                      onChange={(e) => setCryptoCustom(e.target.value.replace(/[^0-9.]/g, ''))}
                      placeholder="например, 5"
                      className="flex-1 rounded-2xl px-4 py-3 text-base outline-none"
                      style={{
                        background: 'var(--color-glass)',
                        border: '1px solid color-mix(in oklab, var(--color-gold) 25%, transparent)',
                        color: 'var(--color-ivory)',
                        fontFamily: 'var(--font-display)',
                      }}
                    />
                    <button
                      disabled={cryptoBusy || cryptoCustomAmount === null}
                      onClick={() => cryptoCustomAmount !== null && void launchCrypto(cryptoCustomAmount)}
                      className="btn-primary"
                      style={{ minWidth: 90 }}
                    >
                      В CryptoBot
                    </button>
                  </div>
                  <p className="text-[11px] text-ivory/45">
                    Откроется счёт в @CryptoBot. После оплаты фишки зачислятся автоматически
                    (~85 фишек/$, 1 фишка ≈ 1 ₽).
                  </p>
                </div>
              </>
            )}

            {tab === 'ton' && (
              <>
                {tonIntent === null ? (
                  <>
                    <div className="flex flex-col gap-2">
                      <label className="text-xs uppercase tracking-wider text-ivory/55">
                        Сколько TON хотите внести
                      </label>
                      <input
                        type="number"
                        inputMode="decimal"
                        step="any"
                        min={0}
                        value={tonAmount}
                        onChange={(e) => setTonAmount(e.target.value.replace(/[^0-9.]/g, ''))}
                        placeholder="например, 1"
                        className="rounded-2xl px-4 py-3 text-base outline-none"
                        style={{
                          background: 'var(--color-glass)',
                          border: '1px solid color-mix(in oklab, var(--color-gold) 25%, transparent)',
                          color: 'var(--color-ivory)',
                          fontFamily: 'var(--font-display)',
                        }}
                      />
                      <p className="text-[11px] text-ivory/45">
                        Это просто ориентир — реально зачислим то, сколько придёт на наш адрес.
                      </p>
                    </div>
                    <button
                      disabled={tonBusy || tonAmountValue === null}
                      onClick={() => void generateTonIntent()}
                      className="btn-primary"
                    >
                      {tonBusy ? 'Генерируем…' : 'Получить адрес для оплаты'}
                    </button>
                  </>
                ) : (
                  <>
                    <p className="text-xs text-ivory/70">
                      Из любого TON-кошелька (Tonkeeper, Wallet и т.п.) отправьте TON на адрес ниже,
                      <strong> обязательно</strong> с комментарием — без него зачисление не состоится.
                    </p>
                    <CopyRow label="Адрес" value={tonIntent.address} onCopy={() => void copyToClipboard(tonIntent.address)} />
                    <CopyRow label="Комментарий" value={tonIntent.comment} onCopy={() => void copyToClipboard(tonIntent.comment)} />
                    <p className="text-[11px] text-ivory/55 flex items-center gap-1 flex-wrap">
                      Зачисление произойдёт автоматически в течение 1-2 минут после подтверждения сети
                      (~ {tonIntent.expectedStars} <ChipIcon size={11} /> при указанной сумме).
                    </p>
                    <button onClick={() => setTonIntent(null)} className="btn-ghost text-sm">
                      Сгенерировать другой
                    </button>
                  </>
                )}
              </>
            )}

            <button
              onClick={onClose}
              className="text-center text-sm text-ivory/55 underline-offset-2 hover:underline"
            >
              Закрыть
            </button>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}

function CopyRow({ label, value, onCopy }: { label: string; value: string; onCopy: () => void }) {
  return (
    <div className="flex flex-col gap-1">
      <span className="text-[10px] uppercase tracking-wider text-ivory/45">{label}</span>
      <button
        onClick={onCopy}
        className="flex items-center justify-between gap-2 rounded-2xl px-3 py-2.5 text-left text-sm"
        style={{
          background: 'var(--color-glass)',
          border: '1px solid color-mix(in oklab, var(--color-gold) 30%, transparent)',
          fontFamily: 'var(--font-display)',
          color: 'var(--color-ivory)',
        }}
      >
        <span className="truncate">{value}</span>
        <span className="shrink-0 text-xs text-gold">копировать</span>
      </button>
    </div>
  );
}
